// Module Import
//---------------------------------
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import axios from 'axios'
import { graphql, useStaticQuery } from 'gatsby'
import Modal from '../Modal/Modal'
import EnquireForm from '../Forms/EnquireForm'
import { useToast } from '../../../context/ToastContext'
import TypographyAtom from '../../atoms/typography/typography'
import validateCaptchaToken from '../../../utils/captcha/validateCaptchaToken'
import { enquireFormSchema } from '../../../utils/form/validationSchema'
import useFormValidation from '../../../hooks/useFormValidation'
import useCustomer from '../../../context/CustomerContext'
import ButtonAtom from '../../atoms/buttons/buttons'

// Component Import
//---------------------------------
const ResponseMessage = styled.div`
  display: grid;
  place-content: center;
  margin: 30px 0;

  h4 {
    text-align: center;
  }
`

// Styled Component Declarations
//---------------------------------

const defaultValues = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  clientQuery: '',
}

// Component Export
//---------------------------------
export default function EnquireModal({ isOpen, onClose, shouldApplyCourse, freeCourseRef }) {
  const data = useStaticQuery(graphql`
    query {
      contentfulPageProductInformation(productHandle: { eq: "dji-zenmuse-l1-workflow-course" }) {
        l1WorkflowCourseVideoList {
          title
          link
          emailPoster
        }
      }
    }
  `)
  const { customer, optimisticSubmitFreeCourse } = useCustomer()
  const [formValues, setFormValues] = useState(defaultValues)
  const [loading, setLoading] = useState(false)
  const [isSent, setIsSent] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [formRef, setFormRef] = useState(null)
  const [captchaRef, setCaptchaRef] = useState(null)
  const { showToast } = useToast()
  const { validateValues, validateFieldOnChange, errors, resetErrors } = useFormValidation({
    schema: enquireFormSchema({ isClientQueryNotRequired: shouldApplyCourse }),
    formRef,
  })

  const courseList = data?.contentfulPageProductInformation?.l1WorkflowCourseVideoList || []

  const handleOpenCourse = () => {
    onClose()
    freeCourseRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const onSubmit = useCallback(async () => {
    setLoading(true)

    const validatedData = await validateValues(formValues)

    if (!validatedData) {
      showToast('Validation error', '')
      setLoading(false)
      return
    }

    const isCaptchaValid = await validateCaptchaToken(captchaToken, showToast, setLoading)

    if (!isCaptchaValid) {
      if (captchaRef)
        captchaRef.current.captcha.scrollIntoView({ behavior: 'smooth', block: 'center' })

      return
    }

    let error = false

    const formData = new FormData()
    Object.entries(validatedData).forEach(([key, value]) => {
      formData.append(key, value)
    })

    formData.set('page[title]', document.title)
    formData.set('page[href]', document.URL)
    formData.set('phone', validatedData.phone.length < 6 ? '' : validatedData.phone)

    if (shouldApplyCourse) {
      formData.set('L1Course', 'on')
      formData.set('courseList', JSON.stringify(courseList))

      if (customer?.id) {
        formData.set('customerId', customer.id)
      }
    }

    const response = await axios
      .post(`${process.env.GATSBY_NETLIFY_API_URL}/mail_enquire`, formData)
      .catch((err) => {
        showToast(
          'Message unsuccessfully sent',
          err?.response?.data?.apiErrorStatus === 409
            ? 'The phone number is already in use by another profile'
            : err?.response?.data?.error || 'Failed to send email'
        )
        return null
      })

    if (response && shouldApplyCourse) {
      optimisticSubmitFreeCourse()
    }

    if (response && typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'gaTriggerEvent',
        gaEventCategory: 'enquire_now',
        gaEventAction: 'enquire_now',
      })
    }

    if (!response) {
      error = true
    }

    setLoading(false)
    setIsSent(!error)
  }, [formValues, captchaToken])

  const onChange = useCallback(
    async ({ target: { name, value } }) => {
      setFormValues((prev) => ({ ...prev, [name]: value }))

      await validateFieldOnChange({ name, value })
    },
    [errors]
  )

  const onCloseModal = useCallback(() => {
    setFormValues(defaultValues)
    resetErrors()
    setLoading(false)

    onClose()
  }, [onClose])

  return (
    <Modal
      title={shouldApplyCourse ? 'Access L1 Course' : 'Ask Us'}
      subTitle={
        shouldApplyCourse
          ? 'Complete the short form below to unlock your L1 course. You will receive an email containing the training videos. You will also be able to access the content on this L1 course page if logged in to the site.'
          : 'Fill out the short form below to contact our expert sales team who can answer any queries you have on this product.'
      }
      isLoading={loading}
      isOpen={isOpen}
      onClose={onCloseModal}
      onSubmit={onSubmit}
      primaryButtonText="Submit"
      disableButtons={isSent}
    >
      {!isSent ? (
        <EnquireForm
          errors={errors}
          loading={loading}
          formValues={formValues}
          onChangeCaptcha={setCaptchaToken}
          onChange={onChange}
          onSubmit={onSubmit}
          setFormRef={setFormRef}
          setCaptchaRef={setCaptchaRef}
          shouldApplyCourse={shouldApplyCourse}
        />
      ) : (
        <ResponseMessage>
          {shouldApplyCourse ? (
            <ButtonAtom
              buttonType="Primary Button"
              buttonCopy="Open course"
              onClick={handleOpenCourse}
            />
          ) : (
            <TypographyAtom
              weight="Light"
              type="H4"
              copy="Thank you for contacting us! We will reply you as soon as possible"
            />
          )}
        </ResponseMessage>
      )}
    </Modal>
  )
}

EnquireModal.propTypes = {}
